import './RichMarkdownEditor.css';
import 'katex/dist/katex.min.css';
import '@/components/tiptap-extensions/mermaid-block.scss';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension, InputRule } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell as BaseTableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Underline from '@tiptap/extension-underline';
import { BubbleMenu } from '@tiptap/react/menus';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import { Markdown } from '@tiptap/markdown';
import type { Editor } from '@tiptap/react';
import { Mathematics, migrateMathStrings } from '@tiptap/extension-mathematics';
import { MermaidDiagram } from '@/components/tiptap-extensions/mermaid-block';
import { MermaidIcon } from '@/components/tiptap-icons/mermaid-icon';
import { MathIcon } from '@/components/tiptap-icons/math-icon';
import { CharacterCount } from '@tiptap/extensions';
import { marked } from 'marked';
import { TextSelection } from '@tiptap/pm/state';
import { SensitiveDataMark } from './SensitiveDataMark';
import { SensitiveDataContextMenu } from './SensitiveDataContextMenu';
import { encodeSensitiveData } from '../../../../api/services/artifacts';
import { uploadWorkspaceMedia } from '../../../../api/services/knowledge';
import {
  CitationNode,
  EditorPlugins,
  GapNode,
  computeParagraphConfidences,
} from './extensions';

const TableExtended = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      borderStyle: {
        // Keep null as default and ALWAYS render data-border so CSS can reliably
        // style grid/rows/cols/none even for default tables.
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-border') || 'grid',
        renderHTML: (attributes: Record<string, unknown>) => ({
          'data-border': (attributes.borderStyle as string | null) ?? 'grid',
        }),
      },
    };
  },
});

// More permissive inline math regex than the default from the extension.
// We want to aggressively migrate any `$...$` LaTeX we see in scientific
// documents, even if it looks a bit like currency – the user cares much
// more about complete math rendering than about the rare false positive.
const INLINE_MATH_REGEX = /\$([^$]+)\$/g;

/** Input rule: typing $...$ converts to inline math node (single dollar). */
const InlineMathSingleDollarInputRule = Extension.create({
  name: 'inlineMathSingleDollarInputRule',
  addInputRules() {
    return [
      new InputRule({
        find: /(^|[^$])(\$([^$\n]+?)\$)(?!\$)/,
        handler: ({ state, range, match }) => {
          const inlineMath = state.schema.nodes.inlineMath;
          if (!inlineMath) return null;
          const latex = (match[3] ?? '').trim();
          const start = range.from + (match[1]?.length ?? 0);
          const end = range.to;
          state.tr.replaceWith(start, end, inlineMath.create({ latex }));
        },
      }),
    ];
  },
});

type RichContentFormat = 'markdown' | 'richtext' | 'tiptap_json';
type RichValue = string | Record<string, unknown>;

type RichMarkdownEditorProps = {
  value: RichValue;
  format?: RichContentFormat;
  onChange: (content: RichValue, format: RichContentFormat) => void;
  artifactId?: string;
  saveArtifactImmediately?: (
    id: string,
    updates: { content: RichValue; format?: RichContentFormat },
  ) => Promise<void>;
  highlightLine?: number;
  highlightEndLine?: number;
  highlightText?: string; // The actual text to highlight (from citation.text)
  /** Called when character/word count changes (for footer display). */
  onCharacterCountChange?: (counts: { characters: number; words: number }) => void;
};

const mentionItems = [
  { id: 'alice', label: 'Alice (PM)' },
  { id: 'ben', label: 'Ben (Eng)' },
  { id: 'charlie', label: 'Charlie (Design)' },
  { id: 'doc-roadmap', label: 'Doc: Roadmap' },
  { id: 'doc-notes', label: 'Doc: Meeting Notes' },
];

const ToolbarButton: React.FC<{
  onClick: () => void;
  active?: boolean;
  label: string;
  title?: string;
  icon?: React.ReactNode;
}> = ({ onClick, active, label, title, icon }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-1 text-xs rounded border border-neutral-200 bg-white hover:bg-neutral-100 transition flex items-center gap-1 ${
      active ? 'text-blue-600 border-blue-300 bg-blue-50' : 'text-neutral-700'
    }`}
    aria-label={title || label}
    title={title || label}
  >
    {icon && <span className="w-3 h-3 flex-shrink-0">{icon}</span>}
    {label}
  </button>
);

/** Migrate display math $$...$$ in paragraphs to blockMath nodes. Run after migrateMathStrings. */
function migrateBlockMathStrings(editor: Editor): void {
  const { schema } = editor.state;
  const blockMath = schema.nodes.blockMath;
  if (!blockMath) return;

  const tr = editor.state.tr;
  const displayMathRegex = /^\s*\$\$([\s\S]+?)\$\$\s*$/;
  let hasChanges = false;

  tr.doc.descendants((node, pos) => {
    if (node.type.name !== 'paragraph' || !node.textContent) return;
    const trimmed = node.textContent.trim();
    const match = trimmed.match(displayMathRegex);
    if (!match) return;

    const from = tr.mapping.map(pos);
    const to = tr.mapping.map(pos + node.nodeSize);
    const $from = tr.doc.resolve(from);
    const parent = $from.parent;
    const index = $from.index();
    if (!parent.canReplaceWith(index, index + 1, blockMath)) return;

    const latex = match[1].trim();
    tr.replaceWith(from, to, blockMath.create({ latex }));
    hasChanges = true;
  });

  if (hasChanges) {
    tr.setMeta('addToHistory', false);
    editor.view.dispatch(tr);
  }
}

/** Migrate code blocks with language "mermaid" to mermaidDiagram nodes. Run after math migrations. */
function migrateMermaidCodeBlocks(editor: Editor): void {
  const { schema } = editor.state;
  const mermaidDiagram = schema.nodes.mermaidDiagram;
  if (!mermaidDiagram) return;

  const tr = editor.state.tr;
  let hasChanges = false;

  tr.doc.descendants((node, pos) => {
    if (node.type.name !== 'codeBlock') return;
    const lang = (node.attrs.language as string | undefined) ?? '';
    if (lang.toLowerCase() !== 'mermaid') return;

    const from = tr.mapping.map(pos);
    const to = tr.mapping.map(pos + node.nodeSize);
    const $from = tr.doc.resolve(from);
    const parent = $from.parent;
    const index = $from.index();
    if (!parent.canReplaceWith(index, index + 1, mermaidDiagram)) return;

    const code = node.textContent?.trim() ?? '';
    tr.replaceWith(from, to, mermaidDiagram.create({ code }));
    hasChanges = true;
  });

  if (hasChanges) {
    tr.setMeta('addToHistory', false);
    editor.view.dispatch(tr);
  }
}

// Helper function to detect if content is markdown
const isMarkdown = (content: string): boolean => {
  if (!content || content.trim() === '') return false;
  // Check for common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s+.+$/m, // Headers
    /\*\*.*?\*\*/, // Bold
    /\*.*?\*/, // Italic
    /^[-*+]\s+.+$/m, // Lists
    /^\d+\.\s+.+$/m, // Numbered lists
    /^>\s+.+$/m, // Blockquotes
    /```[\s\S]*?```/, // Code blocks
    /`[^`]+`/, // Inline code
    /\[.*?\]\(.*?\)/, // Links
    /^\|.*\|.*\|$/m, // Tables
  ];
  return markdownPatterns.some((pattern) => pattern.test(content));
};

// Detect if content is HTML (starts with HTML tags)
const isHTML = (content: string): boolean => {
  if (!content || content.trim() === '') return false;
  return content.trim().startsWith('<');
};

function reportCharacterCount(
  editor: Editor,
  cb: ((counts: { characters: number; words: number }) => void) | undefined,
) {
  if (!cb) return;
  const storage = editor.storage?.characterCount;
  if (!storage?.characters || !storage?.words) return;
  cb({ characters: storage.characters(), words: storage.words() });
}

export const RichMarkdownEditor: React.FC<RichMarkdownEditorProps> = ({
  value,
  format = 'markdown',
  onChange,
  artifactId,
  saveArtifactImmediately,
  highlightLine,
  highlightEndLine,
  highlightText,
  onCharacterCountChange,
}) => {
  const editorRef = React.useRef<Editor | null>(null);
  const onCharacterCountChangeRef = React.useRef(onCharacterCountChange);
  onCharacterCountChangeRef.current = onCharacterCountChange;
  const isInternalUpdateRef = React.useRef(false); // Track if update is from user editing
  const lastValueRef = React.useRef<string>(typeof value === 'string' ? value || '' : ''); // Track last synced HTML string value
  const lastJsonRef = React.useRef<Record<string, unknown> | null>(
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null,
  ); // Track last synced TipTap JSON value
  const emitDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const detectAndMarkSensitiveDataRef = React.useRef<(() => Promise<void>) | null>(null);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    selectedText: string;
    from: number;
    to: number;
  } | null>(null);

  // Slash command menu: show block picker when user types "/" at line start
  const [slashMenu, setSlashMenu] = useState<{ visible: true; pos: number } | null>(null);
  const [slashMenuAnchor, setSlashMenuAnchor] = useState<{ top: number; left: number } | null>(
    null,
  );
  const openSlashMenuRef = React.useRef<(pos: number) => void>(() => {});
  const slashMenuVisibleRef = React.useRef(false);
  openSlashMenuRef.current = (pos: number) => {
    slashMenuVisibleRef.current = true;
    setSlashMenu({ visible: true, pos });
  };
  const slashMenuListRef = React.useRef<HTMLDivElement>(null);

  // Hidden file input for image upload
  const imageFileInputRef = React.useRef<HTMLInputElement>(null);

  const formatRef = React.useRef<RichContentFormat>(format);
  formatRef.current = format;

  const emitChange = useCallback(
    (ed: Editor) => {
      // TipTap JSON persistence: when in JSON mode, emit the full doc.
      if (formatRef.current === 'tiptap_json') {
        const json = ed.getJSON() as Record<string, unknown>;
        onChange(json, 'tiptap_json');
        lastJsonRef.current = json;
        try {
          lastValueRef.current = ed.getHTML().trim();
        } catch {
          lastValueRef.current = '';
        }
        return;
      }

      let html = ed.getHTML();

      // Wrap any coded values that aren't already in spans
      const codedValuePattern =
        /(?:\+|\b)(iban|phone|email|credit_card|address|tax_id|name)_[a-f0-9]{8}_\d{3}\b/gi;

      let lastIndex = 0;
      const parts: string[] = [];
      let match;
      codedValuePattern.lastIndex = 0;

      while ((match = codedValuePattern.exec(html)) !== null) {
        const beforeMatch = html.substring(lastIndex, match.index);
        const codedValue = match[0];
        const type = match[1].toLowerCase();

        const beforeContext = html.substring(Math.max(0, match.index - 100), match.index);
        const afterContext = html.substring(
          match.index + codedValue.length,
          Math.min(html.length, match.index + codedValue.length + 100),
        );
        const fullContext = beforeContext + codedValue + afterContext;

        const isAlreadyWrapped =
          fullContext.includes(`<span data-sensitive-type="${type}"`) ||
          fullContext.includes(`data-sensitive-type="${type}"`);

        if (isAlreadyWrapped) {
          parts.push(beforeMatch);
          parts.push(codedValue);
        } else {
          parts.push(beforeMatch);
          parts.push(
            `<span data-sensitive-type="${type}" class="sensitive-data-highlight">${codedValue} </span>`,
          );
        }

        lastIndex = match.index + codedValue.length;
      }

      parts.push(html.substring(lastIndex));
      html = parts.join('');

      // Always emit HTML in legacy/HTML modes
      const trimmed = html.trim();
      onChange(html, 'richtext');
      // Keep lastValueRef in sync so the sync effect does not overwrite with stale prop
      lastValueRef.current = trimmed;
    },
    [onChange],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: {},
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        underline: false, // register Underline once below to avoid duplicate extension warning
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Markdown.configure({}),
      Mathematics.configure({
        katexOptions: {
          throwOnError: false,
          macros: {
            '\\R': '\\mathbb{R}',
            '\\N': '\\mathbb{N}',
            '\\Z': '\\mathbb{Z}',
          },
        },
        inlineOptions: {
          onClick: (_node: { attrs?: { latex?: string } }, pos: number) => {
            const latex = window.prompt('Edit inline math:', _node.attrs?.latex ?? '');
            if (latex != null && editorRef.current) {
              (editorRef.current as any)
                .chain()
                .setNodeSelection(pos)
                .updateInlineMath({ latex })
                .focus()
                .run();
            }
          },
        },
        blockOptions: {
          onClick: (_node: { attrs?: { latex?: string } }, pos: number) => {
            const latex = window.prompt('Edit block math:', _node.attrs?.latex ?? '');
            if (latex != null && editorRef.current) {
              (editorRef.current as any)
                .chain()
                .setNodeSelection(pos)
                .updateBlockMath({ latex })
                .focus()
                .run();
            }
          },
        },
      }),
      InlineMathSingleDollarInputRule,
      MermaidDiagram,
      CharacterCount,
      SensitiveDataMark,
      CitationNode,
      GapNode,
      EditorPlugins,
      TableExtended.configure({
        HTMLAttributes: { class: 'tiptap-editor-table' },
        resizable: true,
        lastColumnResizable: true,
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: { class: 'tiptap-editor-header' },
      }),
      BaseTableCell.configure({
        HTMLAttributes: { class: 'tiptap-editor-cell' },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          style: 'display: block; max-width: 100%; height: auto; margin: 0.5em 0;',
        },
        resize: {
          enabled: true,
          minWidth: 60,
          minHeight: 40,
          alwaysPreserveAspectRatio: true,
        },
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention-chip',
        },
        suggestion: {
          items: ({ query }: { query: string }) =>
            mentionItems.filter((item) =>
              item.label.toLowerCase().includes((query ?? '').toLowerCase()),
            ),
          render: () => {
            let component: HTMLDivElement | null = null;
            return {
              onStart: (props: any) => {
                component = document.createElement('div');
                component.className = 'mention-list';
                component.innerHTML = props.items
                  .map(
                    (item: { id: string; label: string }) =>
                      `<button data-id="${item.id}" class="mention-list-item">${item.label}</button>`,
                  )
                  .join('');
                component.querySelectorAll('button').forEach((btn) => {
                  btn.addEventListener('click', () => {
                    const id = (btn as HTMLButtonElement).dataset.id;
                    const picked = props.items.find((i: { id: string }) => i.id === id);
                    if (picked) {
                      props.command({ id: picked.id, label: picked.label });
                    }
                  });
                });
                return component;
              },
              onUpdate: (props: any) => {
                if (!component) return;
                component.innerHTML = props.items
                  .map(
                    (item: { id: string; label: string }) =>
                      `<button data-id="${item.id}" class="mention-list-item">${item.label}</button>`,
                  )
                  .join('');
                component.querySelectorAll('button').forEach((btn) => {
                  btn.addEventListener('click', () => {
                    const id = (btn as HTMLButtonElement).dataset.id;
                    const picked = props.items.find((i: { id: string }) => i.id === id);
                    if (picked) {
                      props.command({ id: picked.id, label: picked.label });
                    }
                  });
                });
              },
              onExit: () => {
                component?.remove();
              },
            };
          },
        },
      }),
    ],
    content: typeof value === 'string' ? value || '' : '',
    editorProps: {
      attributes: {
        class:
          'tiptap prose prose-sm max-w-none focus:outline-none min-h-[300px] whitespace-pre-wrap break-words',
      },
      handleKeyDown: (view, event) => {
        if (event.key === '/') {
          const { selection } = view.state;
          const $anchor = selection.$anchor;
          // Start of current block (paragraph, heading, cell, etc.) so "/" works on every line
          const blockStart = $anchor.start($anchor.depth);
          const textFromBlockStart = view.state.doc.textBetween(blockStart, selection.from);
          if (/^\s*$/.test(textFromBlockStart)) {
            event.preventDefault();
            openSlashMenuRef.current?.(selection.from);
            return true;
          }
        }
        if (event.key === 'Escape' && slashMenuVisibleRef.current) {
          slashMenuVisibleRef.current = false;
          setSlashMenu(null);
          setSlashMenuAnchor(null);
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          // Handle clicks to place cursor AFTER sensitive data spans (outside the span)
          const target = event.target as HTMLElement;

          // Check if we clicked on a sensitive data span
          const sensitiveSpan = target.closest('[data-sensitive-type]') as HTMLElement;

          if (sensitiveSpan) {
            // Get the bounding box of the span
            const spanRect = sensitiveSpan.getBoundingClientRect();
            const clickX = event.clientX;

            // Check if click is on the right side of the span (after the text and lock icon)
            // We'll consider clicks in the right 30% of the span as "after" clicks
            const spanWidth = spanRect.right - spanRect.left;
            const clickOffsetFromLeft = clickX - spanRect.left;
            const isClickOnRightSide = clickOffsetFromLeft > spanWidth * 0.7;

            if (isClickOnRightSide) {
              // Find the start position of the span in the document
              const startPos = view.posAtDOM(sensitiveSpan, 0);
              if (startPos !== null) {
                // Resolve the position and find the end of the marked content
                const $start = view.state.doc.resolve(startPos);

                // Walk through the document to find the end of the marked text
                let currentPos = startPos;
                let $pos = $start;

                // Find all consecutive text nodes with the sensitive data mark
                while ($pos.pos < view.state.doc.content.size) {
                  const node = $pos.nodeAfter;
                  if (!node) break;

                  // Check if this node has the sensitive data mark
                  const hasMark = node.marks.some((m) => m.type.name === 'sensitiveData');
                  if (hasMark && node.isText) {
                    currentPos = $pos.pos + node.nodeSize;
                    $pos = view.state.doc.resolve(currentPos);
                  } else {
                    // No more marked nodes, we've found the end
                    break;
                  }
                }

                // Place cursor right after the marked content (outside the span)
                const tr = view.state.tr;
                const selection = TextSelection.create(tr.doc, currentPos);
                tr.setSelection(selection);

                // Remove any stored mark from this position
                const sensitiveMarkType = view.state.schema.marks.sensitiveData;
                if (sensitiveMarkType) {
                  tr.removeStoredMark(sensitiveMarkType);
                }

                view.dispatch(tr);
                event.preventDefault();
                return true; // We handled the click
              }
            }
            // If click is on the left/middle of the span, let ProseMirror handle it normally
            // This allows editing inside the span
          }

          return false; // Let ProseMirror handle the click normally
        },
        contextmenu: (view, event) => {
          const { selection } = view.state;
          const { from, to } = selection;

          if (from !== to) {
            // Text is selected
            const selectedText = view.state.doc.textBetween(from, to);
            if (selectedText.trim()) {
              event.preventDefault();
              setContextMenu({
                x: event.clientX,
                y: event.clientY,
                selectedText: selectedText.trim(),
                from,
                to,
              });
              return true;
            }
          }
          return false;
        },
        keydown: (view, event) => {
          // Slash and Escape are handled in handleKeyDown above
          // Remove sensitive data mark ONLY when typing at the end boundary of marked text
          // This prevents new text from getting the mark applied
          // But allow editing when typing inside the marked text (mark stays)
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            // Regular character key pressed
            const { selection } = view.state;
            if (selection.empty) {
              const $pos = view.state.doc.resolve(selection.from);
              const sensitiveMark = $pos.marks().find((m) => m.type.name === 'sensitiveData');

              // Only remove mark if we're at the EXACT end boundary of the marked text
              // Check both nodeBefore and nodeAfter to be precise
              if (sensitiveMark) {
                const nodeBefore = $pos.nodeBefore;
                const nodeAfter = $pos.nodeAfter;

                // We're at the end boundary if:
                // 1. We have a text node before us AND we're at its end
                // 2. AND there's no text node after us (or it's not marked)
                const atEndOfMarkedNode =
                  nodeBefore &&
                  nodeBefore.isText &&
                  $pos.textOffset === nodeBefore.textContent.length;

                const nextNodeNotMarked =
                  !nodeAfter ||
                  !nodeAfter.isText ||
                  !nodeAfter.marks.some((m) => m.type.name === 'sensitiveData');

                // Only remove mark if we're at the end boundary AND next node isn't marked
                if (atEndOfMarkedNode && nextNodeNotMarked) {
                  // At the end boundary - remove mark so new text doesn't get it
                  const tr = view.state.tr;
                  const sensitiveMarkType = view.state.schema.marks.sensitiveData;
                  if (sensitiveMarkType) {
                    tr.removeStoredMark(sensitiveMarkType);
                    view.dispatch(tr);
                  }
                }
                // If inside the mark (not at end), allow editing with mark (don't remove)
              }
            }
          }

          // Trigger detection and onChange when Enter is pressed (newline)
          if (event.key === 'Enter') {
            // Wait for Enter transaction to complete, then detect and save
            setTimeout(async () => {
              if (editorRef.current && detectAndMarkSensitiveDataRef.current) {
                // Run detection and wait for marks to be applied and serialized
                await detectAndMarkSensitiveDataRef.current();
                // Now marks are in DOM, safe to save
                if (editorRef.current) {
                  emitChange(editorRef.current);
                }
              }
            }, 0);
          }
          return false;
        },
        blur: () => {
          // Save when editor loses focus; cancel debounce and emit immediately
          if (emitDebounceRef.current) {
            clearTimeout(emitDebounceRef.current);
            emitDebounceRef.current = null;
          }
          if (editorRef.current) {
            emitChange(editorRef.current);
          }
        },
      },
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        const text = clipboardData.getData('text/plain');
        if (!text || !text.trim()) return false;

        // Check if the pasted text looks like markdown
        const markdownPatterns = [
          /^#{1,6}\s+.+$/m, // Headers
          /\*\*.*?\*\*/, // Bold
          /\*.*?\*/, // Italic
          /^[-*+]\s+.+$/m, // Lists
          /^\d+\.\s+.+$/m, // Numbered lists
          /^>\s+.+$/m, // Blockquotes
          /```[\s\S]*?```/, // Code blocks
          /`[^`]+`/, // Inline code
          /\[.*?\]\(.*?\)/, // Links
          /^\|.*\|.*\|$/m, // Tables
        ];

        const isMarkdown = markdownPatterns.some((pattern) => pattern.test(text));

        if (isMarkdown && editorRef.current) {
          event.preventDefault();
          // Use TipTap's markdown parsing via insertContent with markdown contentType
          editorRef.current.commands.insertContent(text, {
            contentType: 'markdown',
          } as any);
          return true;
        }

        return false;
      },
    },
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor }) => {
      // Mark this as an internal update to prevent cursor jumping
      isInternalUpdateRef.current = true;

      // Update lastValueRef so the sync effect does not overwrite with stale value prop
      // (e.g. after inserting a table, value is still old until parent re-renders)
      try {
        lastValueRef.current = editor.getHTML().trim();
      } catch {
        lastValueRef.current = editor.getHTML().trim();
      }

      // Debounced persist: emit change after typing stops so stored artifact stays in sync
      if (emitDebounceRef.current) clearTimeout(emitDebounceRef.current);
      emitDebounceRef.current = setTimeout(() => {
        emitDebounceRef.current = null;
        emitChange(editor);
      }, 500);

      // Reset flag after a short delay to allow useEffect to check it
      setTimeout(() => {
        isInternalUpdateRef.current = false;
      }, 0);
      const paragraphConfidences = computeParagraphConfidences(editor);
      editor.commands.updateConfidenceDecorations(paragraphConfidences);
      reportCharacterCount(editor, onCharacterCountChangeRef.current);
    },
    onCreate: ({ editor }) => {
      editorRef.current = editor;
      reportCharacterCount(editor, onCharacterCountChangeRef.current);
      // Convert existing $...$ / $$...$$ text to proper math nodes.
      migrateMathStrings(editor, INLINE_MATH_REGEX);
      migrateBlockMathStrings(editor);
      migrateMermaidCodeBlocks(editor);
      editor.commands.updateConfidenceDecorations(computeParagraphConfidences(editor));
      // Update lastValueRef so the sync effect does not re-set content on first run
      try {
        lastValueRef.current = editor.getHTML().trim();
      } catch {
        /* ignore */
      }
    },
    onTransaction: ({ editor, transaction }) => {
      // Prevent sensitive data mark from extending when typing at the end boundary
      // But allow editing inside the mark (inclusive: true allows this)
      if (!transaction.selection || !transaction.selection.empty) return;

      const $pos = editor.state.doc.resolve(transaction.selection.from);
      const sensitiveMark = $pos.marks().find((m) => m.type.name === 'sensitiveData');

      if (!sensitiveMark) return;

      // Check if we're at the EXACT end of a marked text node
      const nodeBefore = $pos.nodeBefore;
      const atEndOfMarkedNode =
        nodeBefore && nodeBefore.isText && $pos.textOffset === nodeBefore.textContent.length;

      // Check if next position also has the mark (mark continues)
      const $nextPos = editor.state.doc.resolve(
        Math.min(transaction.selection.from + 1, editor.state.doc.content.size),
      );
      const markContinues = $nextPos.marks().some((m) => m.type.name === 'sensitiveData');

      // Only remove mark if we're at the end AND mark doesn't continue
      if (atEndOfMarkedNode && !markContinues) {
        // Check if transaction is inserting text
        const insertsText = transaction.steps.some((step: any) => {
          return step.slice && step.slice.content && step.slice.content.size > 0;
        });

        if (insertsText) {
          // Remove the mark from stored marks to prevent extension
          const tr = editor.state.tr;
          const sensitiveMarkType = editor.schema.marks.sensitiveData;
          if (sensitiveMarkType) {
            tr.removeStoredMark(sensitiveMarkType);
            // Dispatch after current transaction
            setTimeout(() => {
              editor.view.dispatch(tr);
            }, 0);
          }
        }
      }
    },
  });

  // keep external updates in sync
  useEffect(() => {
    if (!editor || value === undefined) return;

    // Skip update if this change came from user editing (prevents cursor jumping)
    if (isInternalUpdateRef.current) {
      return;
    }

    // JSON mode: set TipTap doc directly when value is an object
    if (format === 'tiptap_json' && typeof value === 'object' && value !== null) {
      const serialized = JSON.stringify(value);
      if (lastJsonRef.current && JSON.stringify(lastJsonRef.current) === serialized) {
        return;
      }

      const { from, to } = editor.state.selection;
      isInternalUpdateRef.current = true;
      editor.commands.setContent(value as any);
      lastJsonRef.current = value as Record<string, unknown>;
      try {
        lastValueRef.current = editor.getHTML().trim();
      } catch {
        lastValueRef.current = '';
      }
      setTimeout(() => {
        try {
          const newDoc = editor.state.doc;
          const safeFrom = Math.min(from, newDoc.content.size);
          const safeTo = Math.min(to, newDoc.content.size);
          editor.commands.setTextSelection({ from: safeFrom, to: safeTo });
        } catch {
          editor.commands.focus('end');
        }
        isInternalUpdateRef.current = false;
      }, 0);

      return;
    }

    // Legacy / HTML + markdown modes use string-based content
    if (typeof value !== 'string') {
      return;
    }

    // Normalize newlines first - convert \n escape sequences to actual newlines
    // This handles cases where content comes from JSON/API with escaped newlines
    let normalizedValue = value;
    // Replace literal \n (backslash followed by n) with actual newlines
    // Also handle cases where it might be double-escaped
    normalizedValue = normalizedValue
      .replace(/\\n/g, '\n') // Replace \n with actual newline
      .replace(/\\r\\n/g, '\n') // Replace \r\n with newline
      .replace(/\\r/g, '\n'); // Replace \r with newline

    // Decode HTML entities (handle &amp, &lt, &gt, &quot, etc.)
    // Only decode if content contains HTML entities (not HTML tags)
    if (normalizedValue.includes('&') && !normalizedValue.trim().startsWith('<')) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = normalizedValue;
      normalizedValue = textarea.value;
    }

    // Check if value actually changed from last synced value
    if (normalizedValue === lastValueRef.current) {
      return;
    }

    // Get current editor content for comparison (always HTML)
    const currentContent = editor.getHTML();
    const normalizedCurrent = currentContent.trim();
    const normalizedInput = normalizedValue.trim();

    // Do not overwrite when the editor has the latest content (e.g. user just added a table
    // or typed a new line) and the value prop is stale (parent has not re-rendered yet).
    // This prevents tables disappearing and cursor jumping.
    if (normalizedCurrent !== normalizedInput && normalizedCurrent === lastValueRef.current) {
      return;
    }

    if (normalizedCurrent !== normalizedInput) {
      const { from, to } = editor.state.selection;

      // Content is HTML-only now. For legacy markdown content (backward compat),
      // detect and convert via marked before setting.
      const contentToSet = isHTML(normalizedValue)
        ? normalizedValue
        : isMarkdown(normalizedValue)
          ? (() => {
              try {
                return marked.parse(normalizedValue, { breaks: true, gfm: true });
              } catch {
                return normalizedValue;
              }
            })()
          : normalizedValue;

      isInternalUpdateRef.current = true;
      editor.commands.setContent(contentToSet || '');
      lastValueRef.current = normalizedValue;
      // Run math migrations synchronously so math renders in the same paint
      migrateMathStrings(editor, INLINE_MATH_REGEX);
      migrateBlockMathStrings(editor);
      migrateMermaidCodeBlocks(editor);
      setTimeout(() => {
        try {
          const newDoc = editor.state.doc;
          const safeFrom = Math.min(from, newDoc.content.size);
          const safeTo = Math.min(to, newDoc.content.size);
          editor.commands.setTextSelection({ from: safeFrom, to: safeTo });
        } catch {
          editor.commands.focus('end');
        }
        isInternalUpdateRef.current = false;
      }, 0);
    }
  }, [editor, value, format]);

  // Scroll to text and highlight when highlightText or highlightLine changes
  useEffect(() => {
    if (!editor) return;

    const findAndSelectText = () => {
      // Strategy 1: Use highlightText for precise matching
      if (highlightText && highlightText.trim()) {
        const searchText = highlightText.trim();
        const doc = editor.state.doc;

        // Build a plain text representation of the document with position mapping
        let fullText = '';
        const textRanges: Array<{ from: number; to: number; startInFullText: number }> = [];

        doc.descendants((node, pos) => {
          if (node.isText && node.text) {
            textRanges.push({
              from: pos,
              to: pos + node.nodeSize,
              startInFullText: fullText.length,
            });
            fullText += node.text;
          } else if (node.isBlock || node.type.name === 'hardBreak') {
            // Add a space or newline for block separators/breaks to prevent merging words
            fullText += ' ';
          }
          return true;
        });

        // Search in the full text
        const variations = [
          searchText, // Original
          searchText.replace(/[*_`~]/g, ''), // Strip common markdown symbols
          searchText.replace(/\s+/g, ' '), // Normalize whitespace
        ];

        let searchIndex = -1;
        let matchedTextLength = 0;
        let mappingFn: ((start: number, end: number) => { start: number; end: number }) | null =
          null;

        // Strategy A: Standard normalization (whitespace/markdown)
        const normalizedFullText = fullText.replace(/\s+/g, ' ').toLowerCase();

        for (const variant of variations) {
          const normalizedVariant = variant.replace(/\s+/g, ' ').toLowerCase();
          const idx = normalizedFullText.indexOf(normalizedVariant);
          if (idx !== -1) {
            searchIndex = idx;
            matchedTextLength = normalizedVariant.length;

            // Standard mapping logic for whitespace normalization
            mappingFn = (sIdx, eIdx) => {
              // eIdx is length here
              let currentNormalizedIndex = 0;
              let originalIndex = 0;

              // Find start
              while (currentNormalizedIndex < sIdx && originalIndex < fullText.length) {
                if (/\s/.test(fullText[originalIndex])) {
                  if (/\s/.test(normalizedFullText[currentNormalizedIndex])) {
                    while (originalIndex < fullText.length && /\s/.test(fullText[originalIndex]))
                      originalIndex++;
                    currentNormalizedIndex++;
                  } else {
                    originalIndex++;
                  }
                } else {
                  currentNormalizedIndex++;
                  originalIndex++;
                }
              }
              const start = originalIndex;

              // Find end
              let currentNormalizedEnd = currentNormalizedIndex;
              let originalEndIndex = originalIndex;
              const targetNormalizedEnd = sIdx + matchedTextLength;

              while (
                currentNormalizedEnd < targetNormalizedEnd &&
                originalEndIndex < fullText.length
              ) {
                if (/\s/.test(fullText[originalEndIndex])) {
                  if (/\s/.test(normalizedFullText[currentNormalizedEnd])) {
                    while (
                      originalEndIndex < fullText.length &&
                      /\s/.test(fullText[originalEndIndex])
                    )
                      originalEndIndex++;
                    currentNormalizedEnd++;
                  } else {
                    originalEndIndex++;
                  }
                } else {
                  currentNormalizedEnd++;
                  originalEndIndex++;
                }
              }
              return { start, end: originalEndIndex };
            };
            break;
          }
        }

        // Strategy B: Ultra-fuzzy (alphanumeric only)
        if (searchIndex === -1) {
          const skeletonFullText = fullText.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
          const skeletonSearch = searchText.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

          if (skeletonSearch.length > 3) {
            // Only use for meaningful queries
            const idx = skeletonFullText.indexOf(skeletonSearch);
            if (idx !== -1) {
              // Build explicit mapping for accuracy
              const map: number[] = []; // skeleton index -> fullText index
              let skelIdx = 0;
              for (let i = 0; i < fullText.length; i++) {
                if (/[a-zA-Z0-9]/.test(fullText[i])) {
                  map[skelIdx] = i;
                  skelIdx++;
                }
              }

              searchIndex = idx;
              matchedTextLength = skeletonSearch.length;
              mappingFn = (sIdx, _len) => {
                const start = map[sIdx];
                // end is mapped index of last char + 1
                // map[sIdx + length - 1] gives index of last matched char. End is that + 1.
                const endCharIndex = map[sIdx + skeletonSearch.length - 1];
                return { start, end: endCharIndex + 1 };
              };
            }
          }
        }

        if (searchIndex !== -1 && mappingFn) {
          // Map back to document positions
          const { start: startInFullText, end: endInFullText } = mappingFn(
            searchIndex,
            matchedTextLength,
          );

          // Find the text node containing the start
          let startRange = textRanges.find(
            (r) =>
              startInFullText >= r.startInFullText &&
              startInFullText < r.startInFullText + (r.to - r.from),
          );

          // If start is in a gap (artificial space), use the next text node
          if (!startRange) {
            startRange = textRanges.find((r) => r.startInFullText > startInFullText);
          }

          // Find the text node containing the end
          const endRange = textRanges.find(
            (r) =>
              endInFullText >= r.startInFullText &&
              endInFullText <= r.startInFullText + (r.to - r.from),
          );

          if (startRange) {
            const startPos =
              startRange.from + Math.max(0, startInFullText - startRange.startInFullText);

            // Calculate endPos
            let endPos = startPos + (endInFullText - startInFullText); // Default if in same node

            if (endRange && endRange !== startRange) {
              // Spans multiple nodes.
              endPos = endRange.from + Math.max(0, endInFullText - endRange.startInFullText);
            } else if (!endRange) {
              // Fallback if end range not found
              if (textRanges.length > 0) {
                const lastRange = textRanges[textRanges.length - 1];
                if (endInFullText > lastRange.startInFullText + (lastRange.to - lastRange.from)) {
                  endPos = doc.content.size;
                } else {
                  endPos = startPos + (endInFullText - startInFullText);
                }
              }
            }

            // Guard against invalid selection endpoints (ProseMirror requires inline content).
            const safeStart = Math.min(Math.max(0, startPos), doc.content.size);
            const safeEnd = Math.min(Math.max(safeStart, endPos), doc.content.size);
            if (doc.resolve(safeStart).parent.inlineContent) {
              const tr = editor.state.tr;
              tr.setSelection(TextSelection.create(doc, safeStart, safeEnd));
              tr.scrollIntoView();
              editor.view.dispatch(tr);
              editor.view.focus(); // Ensure focus for scroll to work
            } else {
              editor.commands.setTextSelection(safeStart);
              editor.commands.scrollIntoView();
            }
            return;
          }
        }
      }

      // Strategy 2: Fallback to line-based scrolling
      if (highlightLine && highlightLine > 0) {
        // Estimate position based on line number
        // Note: TipTap doesn't strictly track "lines" like a code editor,
        // but we can count paragraphs/blocks
        const doc = editor.state.doc;
        let currentLine = 1;
        let foundPos = 0;
        let foundNodeSize = 0;
        let found = false;

        doc.descendants((node, pos) => {
          if (found) return false;

          if (node.isBlock) {
            if (currentLine === highlightLine) {
              foundPos = pos;
              foundNodeSize = node.nodeSize;
              found = true;
              return false;
            }
            currentLine++;
          }
          return true;
        });

        if (found) {
          // Prefer selecting inside inline content to avoid ProseMirror selection warnings.
          const safePos = Math.min(Math.max(0, foundPos + 1), doc.content.size);
          if (doc.resolve(safePos).parent.inlineContent) {
            const tr = editor.state.tr;
            tr.setSelection(TextSelection.create(doc, safePos, safePos));
            tr.scrollIntoView();
            editor.view.dispatch(tr);
            editor.view.focus();
          } else {
            editor.commands.setTextSelection(safePos);
            editor.commands.scrollIntoView();
          }
        }
      }
    };

    // Small delay to ensure editor content is fully loaded/rendered
    const timeoutId = setTimeout(findAndSelectText, 100);
    return () => clearTimeout(timeoutId);
  }, [editor, highlightLine, highlightEndLine, highlightText, value]);

  // Detection function - reusable for both initial load and newline detection
  // Returns a promise that resolves when marks are applied and serialized
  const detectAndMarkSensitiveData = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const ed = editorRef.current;
      if (!ed || !ed.state) {
        resolve();
        return;
      }

      // Patterns for detecting sensitive data
      // CRITICAL ORDER: Coded values FIRST, then real patterns
      // This prevents false positives (e.g., +tax_id being matched as phone)
      const patterns = [
        // 1. Coded values from backend (MUST BE FIRST to avoid false matches)
        // Format: {type}_{8hex}_{3digits} e.g., phone_b660475e_000, iban_74f1a85b_000
        // Can optionally start with + or other prefix, but must have the coded format
        {
          regex:
            /(?:\+|\b)(iban|phone|email|credit_card|address|tax_id|name)_[a-f0-9]{8}_\d{3}\b/gi,
          type: (match: RegExpMatchArray) => match[1].toLowerCase() as string,
        },
        // 2. IBANs - Two letters (country code) + two digits (check) + alphanumeric (15-34 chars total)
        // Must start with 2 uppercase letters, then 2 digits, then alphanumeric
        { regex: /\b(?:IBAN\s*:?\s*)?([A-Z]{2}\d{2}[A-Z0-9]{13,30})\b/g, type: 'iban' },
        // 3. Credit cards - 13-19 digits with optional spaces/dashes
        // Supports: 4xxx (Visa), 5[1-5]xx (MC), 3[47]xx (Amex), 6xxx (Discover)
        // Also matches plain 16-digit numbers
        {
          regex: /\b(?:\d{4}[ -]?){3}\d{1,4}\b/g,
          type: 'credit_card',
        },
        // 4. Email addresses - STRICT: Must have @ and valid domain with TLD
        // The \b word boundary ensures we don't match across paragraph breaks
        {
          regex: /\b[a-zA-Z0-9][a-zA-Z0-9._-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}\b/g,
          type: 'email',
        },
        // 5. Phone numbers - MUST have one of:
        // - International prefix: +
        // - Leading 0 or 00 (European format)
        // - Parentheses around area code: (179)
        // - REQUIRED separators between groups: 123-456-7890
        // This prevents matching plain digit sequences in IBANs
        {
          regex:
            /(?:\+\d{1,3}[\s.-]?(?:\d[\s.-]?){7,14}|\b0{1,2}[\s.-]?(?:\d[\s.-]?){8,14}|\(\d{2,5}\)[\s.-]?(?:\d[\s.-]?){6,11}|\b\d{2,4}[-.\s]\d{2,4}[-.\s]\d{2,6}\b)/g,
          type: 'phone',
        },
      ];

      const doc = ed.state.doc;
      const tr = ed.state.tr;

      // Build text content WITH paragraph boundaries (newlines between blocks)
      // This prevents "hello" + "email@test.com" from being matched as "helloemail@test.com"
      let fullText = '';
      const textNodePositions: Array<{
        start: number;
        end: number;
        docPos: number;
        length: number;
      }> = [];

      doc.descendants((node, pos) => {
        if (node.isText && node.text) {
          textNodePositions.push({
            start: fullText.length,
            end: fullText.length + node.text.length,
            docPos: pos,
            length: node.text.length,
          });
          fullText += node.text;
        } else if (node.isBlock && fullText.length > 0 && !fullText.endsWith('\n')) {
          // Add newline between blocks to prevent word concatenation
          fullText += '\n';
        }
        return true;
      });
      let hasChanges = false;

      // Track matched text ranges to prevent overlapping matches
      // (e.g., phone pattern matching inside an IBAN)
      const matchedRanges: Array<{ start: number; end: number }> = [];

      // Find and mark sensitive data
      patterns.forEach((pattern) => {
        const { regex, type } = pattern;
        // Reset regex lastIndex
        regex.lastIndex = 0;
        const matches = [...fullText.matchAll(regex)];

        matches.forEach((match) => {
          if (match.index === undefined) return;

          // Determine the data type (can be a function for coded values)
          const dataType = typeof type === 'function' ? type(match) : type;

          // Use full match (match[0]) for all patterns - this includes any prefixes like +
          const matchedText = match[0];

          // Check if this match overlaps with an already-matched range
          const matchStart = match.index;
          const matchEnd = match.index + matchedText.length;
          const overlaps = matchedRanges.some(
            (range) => matchStart < range.end && matchEnd > range.start,
          );
          if (overlaps) {
            return; // Skip, already matched by a higher-priority pattern
          }

          // Additional validation to prevent false positives
          if (dataType === 'email') {
            // Email MUST contain @ and . (domain separator)
            if (!matchedText.includes('@') || !matchedText.includes('.')) {
              return; // Skip invalid email matches
            }
            // Email must have valid format: local@domain.tld
            const parts = matchedText.split('@');
            if (parts.length !== 2 || !parts[0] || !parts[1]) {
              return; // Skip invalid email format
            }
            // Domain must contain a dot (for TLD)
            if (!parts[1].includes('.')) {
              return; // Skip invalid email format
            }
            // Local part and domain must not be empty
            if (parts[0].length === 0 || parts[1].length === 0) {
              return; // Skip invalid email format
            }
          } else if (dataType === 'iban') {
            // IBAN must be 15-34 characters after removing spaces
            const cleaned = matchedText.replace(/\s/g, '').toUpperCase();
            if (cleaned.length < 15 || cleaned.length > 34) {
              return; // Skip invalid IBAN
            }
            // Must start with 2 letters (country code)
            if (!/^[A-Z]{2}/.test(cleaned)) {
              return; // Skip invalid IBAN
            }
          } else if (dataType === 'credit_card') {
            // Credit card must be 13-19 digits (after removing spaces/dashes)
            const cleaned = matchedText.replace(/[\s\-]/g, '');
            if (cleaned.length < 13 || cleaned.length > 19 || !/^\d+$/.test(cleaned)) {
              return; // Skip invalid credit card
            }
            // Simple Luhn check - sum of digits with alternating doubling
            let sum = 0;
            let alternate = false;
            for (let i = cleaned.length - 1; i >= 0; i--) {
              let n = parseInt(cleaned[i], 10);
              if (alternate) {
                n *= 2;
                if (n > 9) n -= 9;
              }
              sum += n;
              alternate = !alternate;
            }
            if (sum % 10 !== 0) {
              return; // Skip invalid credit card (failed Luhn)
            }
          } else if (dataType === 'phone') {
            // Phone must have at least 8 digits (after removing non-digits)
            const cleaned = matchedText.replace(/\D/g, '');
            if (cleaned.length < 8 || cleaned.length > 15) {
              return; // Skip invalid phone
            }
          }

          const start = match.index!;
          const end = start + matchedText.length;

          // Find document positions using pre-built text node map
          let docStart = -1;
          let docEnd = -1;

          for (const nodeInfo of textNodePositions) {
            // Check if match starts in this node
            if (start >= nodeInfo.start && start < nodeInfo.end && docStart === -1) {
              docStart = nodeInfo.docPos + (start - nodeInfo.start);
            }
            // Check if match ends in this node
            if (end > nodeInfo.start && end <= nodeInfo.end && docEnd === -1) {
              docEnd = nodeInfo.docPos + (end - nodeInfo.start);
            }
            if (docStart !== -1 && docEnd !== -1) break;
          }

          if (docStart >= 0 && docEnd > docStart) {
            // Record this range as matched to prevent overlapping matches
            matchedRanges.push({ start: match.index!, end: match.index! + matchedText.length });

            // Check if mark already exists at this position
            let markExists = false;
            for (let pos = docStart; pos < docEnd; pos++) {
              const $pos = doc.resolve(pos);
              const existingMark = $pos
                .marks()
                .find((m) => m.type.name === 'sensitiveData' && m.attrs.dataType === dataType);
              if (existingMark) {
                // Check if this mark covers the entire match
                let markEnd = pos;
                for (let checkPos = pos; checkPos < docEnd; checkPos++) {
                  const $check = doc.resolve(checkPos);
                  if ($check.marks().some((m) => m === existingMark)) {
                    markEnd = checkPos + 1;
                  } else {
                    break;
                  }
                }
                if (markEnd >= docEnd) {
                  markExists = true;
                  break;
                }
              }
            }

            if (!markExists) {
              const mark = ed.schema.marks.sensitiveData.create({ dataType: dataType });
              tr.addMark(docStart, docEnd, mark);
              hasChanges = true;
            }
          }
        });
      });

      if (hasChanges) {
        // Dispatch transaction without changing selection
        isInternalUpdateRef.current = true;
        ed.view.dispatch(tr);
        // Wait for marks to be serialized to DOM before resolving
        // Use double requestAnimationFrame to ensure TipTap has serialized marks to HTML
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            isInternalUpdateRef.current = false;
            resolve(); // Resolve after marks are serialized
          });
        });
      } else {
        resolve(); // No changes, resolve immediately
      }
    });
  }, []);

  // Store detection function in ref so it can be accessed from keydown handler
  useEffect(() => {
    detectAndMarkSensitiveDataRef.current = detectAndMarkSensitiveData;
  }, [detectAndMarkSensitiveData]);

  // Auto-detect and highlight sensitive data patterns when content loads from backend
  useEffect(() => {
    if (!editor || !value) return;

    // Skip if this is an internal update (user typing)
    if (isInternalUpdateRef.current) return;

    // Run detection immediately after content is loaded
    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (!editor || !editor.state) return;
      if (isInternalUpdateRef.current) return;

      // Run detection and wait for marks to be applied
      detectAndMarkSensitiveData().then(() => {
        // After marks are serialized, trigger a save to ensure they're persisted
        if (editor && editor.state && !isInternalUpdateRef.current) {
          // Check if we have coded values that need to be marked
          const hasCodedValues =
            /(?:\+|\b)(iban|phone|email|credit_card|address|tax_id|name)_[a-f0-9]{8}_\d{3}\b/gi.test(
              editor.state.doc.textContent,
            );
          if (hasCodedValues) {
            // Force a save to persist marks as richtext
            emitChange(editor);
          }
        }
      });
    }, 50); // Reduced delay for faster detection

    return () => clearTimeout(timeoutId);
  }, [editor, value, detectAndMarkSensitiveData, emitChange]);

  // Position slash menu under cursor when it opens
  useEffect(() => {
    if (!slashMenu?.visible || !editor) return;
    const coords = editor.view.coordsAtPos(slashMenu.pos);
    setSlashMenuAnchor({ top: coords.bottom, left: coords.left });
  }, [slashMenu?.visible, slashMenu?.pos, editor]);

  const e: Editor | null = editor ?? null;

  const toggle = useMemo(
    () => ({
      bold: () => e?.chain().focus().toggleBold().run(),
      italic: () => e?.chain().focus().toggleItalic().run(),
      underline: () => (e?.chain().focus() as any).toggleUnderline().run(),
      code: () => e?.chain().focus().toggleCode().run(),
      heading1: () => e?.chain().focus().toggleHeading({ level: 1 }).run(),
      heading2: () => e?.chain().focus().toggleHeading({ level: 2 }).run(),
      bullet: () => e?.chain().focus().toggleBulletList().run(),
      ordered: () => e?.chain().focus().toggleOrderedList().run(),
      quote: () => e?.chain().focus().toggleBlockquote().run(),
      codeblock: () => e?.chain().focus().toggleCodeBlock().run(),
      table: () => e?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
      addRow: () => e?.chain().focus().addRowAfter().run(),
      addCol: () => e?.chain().focus().addColumnAfter().run(),
      delRow: () => e?.chain().focus().deleteRow().run(),
      delCol: () => e?.chain().focus().deleteColumn().run(),
      delTable: () => e?.chain().focus().deleteTable().run(),
      toggleHeader: () => e?.chain().focus().toggleHeaderRow().run(),
      setTableBorder: (style: 'grid' | 'rows' | 'cols' | 'none') =>
        e?.chain().focus().updateAttributes('table', { borderStyle: style }).run(),
      insertImage: () => imageFileInputRef.current?.click(),
      insertInlineMath: () => {
        if (!e) return;
        const latex = window.prompt('Inline LaTeX (e.g. E = mc^2)', 'E = mc^2');
        if (latex == null) return;
        (e as any).chain().focus().insertInlineMath({ latex }).run();
      },
      insertBlockMath: () => {
        if (!e) return;
        const latex = window.prompt(
          'Display LaTeX (e.g. \\int_0^1 f(x)\\,dx)',
          '\\int_0^1 f(x)\\,dx',
        );
        if (latex == null) return;
        (e as any).chain().focus().insertBlockMath({ latex }).run();
      },
      insertMermaid: (code?: string) => {
        if (!e) return;
        const defaultCode = 'flowchart LR\n  A --> B';
        (e as any)
          .chain()
          .focus()
          .insertMermaid({ code: code ?? defaultCode })
          .run();
      },
      editMath: () => {
        if (!e) return;
        (e as any).chain().focus().insertInlineMath({ latex: 'E = mc^2' }).run();
      },
      setLink: (href: string | null) =>
        (e?.chain().focus() as any).setLink(href ? { href } : { href: null }).run(),
    }),
    [e],
  );

  const handleImageFileSelect = useCallback(
    async (ev: React.ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0];
      if (!file || !e) return;
      try {
        const res = await uploadWorkspaceMedia(file, artifactId ?? '_uploads');
        (e.chain().focus() as any).setImage({ src: res.url }).run();
      } catch (err) {
        console.error('Image upload failed', err);
      }
      ev.target.value = '';
    },
    [e, artifactId],
  );

  const closeSlashMenu = useCallback(() => {
    slashMenuVisibleRef.current = false;
    setSlashMenu(null);
    setSlashMenuAnchor(null);
  }, []);

  const runSlashCommand = useCallback(
    (action: string) => {
      if (!e) return;
      e.chain().focus().run();
      if (action === 'heading1') toggle.heading1();
      else if (action === 'heading2') toggle.heading2();
      else if (action === 'table') toggle.table();
      else if (action === 'image') toggle.insertImage();
      else if (action === 'math') toggle.editMath();
      else if (action === 'mermaid') toggle.insertMermaid();
      else if (action === 'code') toggle.codeblock();
      else if (action === 'bullet') toggle.bullet();
      else if (action === 'ordered') toggle.ordered();
      else if (action === 'quote') toggle.quote();
      else if (action === 'hr') e.chain().focus().setHorizontalRule().run();
      closeSlashMenu();
    },
    [e, toggle, closeSlashMenu],
  );

  const isActive = useMemo(
    () => ({
      bold: e?.isActive('bold'),
      italic: e?.isActive('italic'),
      underline: (e as any)?.isActive('underline'),
      link: (e as any)?.isActive('link'),
      heading1: e?.isActive('heading', { level: 1 }),
      heading2: e?.isActive('heading', { level: 2 }),
      bullet: e?.isActive('bulletList'),
      ordered: e?.isActive('orderedList'),
      quote: e?.isActive('blockquote'),
      codeblock: e?.isActive('codeBlock'),
      code: e?.isActive('code'),
      table: e?.isActive('table'),
      math: e?.isActive('inlineMath') || e?.isActive('blockMath'),
      mermaid: e?.isActive('mermaidDiagram'),
    }),
    [e],
  );

  const tableBorder =
    (e?.isActive('table') && (e?.getAttributes('table')?.borderStyle as string | undefined)) ||
    'grid';

  // Prevent page swipe gestures when scrolling the toolbar horizontally
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  const [isScrollingToolbar, setIsScrollingToolbar] = React.useState(false);
  const touchStartRef = React.useRef<{ x: number; y: number } | null>(null);

  const handleToolbarTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    setIsScrollingToolbar(false);
  };

  const handleToolbarTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    // If horizontal movement is greater than vertical, user is scrolling toolbar
    if (deltaX > deltaY && deltaX > 5) {
      setIsScrollingToolbar(true);
      // Stop propagation to prevent page swipe
      e.stopPropagation();
    }
  };

  const handleToolbarTouchEnd = (e: React.TouchEvent) => {
    if (isScrollingToolbar) {
      // Stop propagation to prevent page swipe
      e.stopPropagation();
    }
    touchStartRef.current = null;
    // Reset after a short delay
    setTimeout(() => setIsScrollingToolbar(false), 100);
  };

  const handleMarkAsSensitive = useCallback(
    async (dataType: string) => {
      if (!editor || !contextMenu || !artifactId || !saveArtifactImmediately) return;

      try {
        // Call backend to create the code in the coding table
        // This stores the code with artifact context so it appears in the sensitive data table
        await encodeSensitiveData({
          text: contextMenu.selectedText,
          data_type: dataType,
          artifact_id: artifactId,
        });

        // Apply the mark to the original selection positions stored when context menu opened
        // This ensures we mark the correct text even if selection has changed
        const { from, to } = contextMenu;
        const mark = editor.schema.marks.sensitiveData.create({ dataType });

        // Create a transaction to apply the mark to the original text
        // We keep the original text visible to the user
        const tr = editor.state.tr;
        tr.addMark(from, to, mark);

        // Temporarily set flag to prevent update loop
        isInternalUpdateRef.current = true;

        // Dispatch transaction and wait for it to complete
        // Use requestAnimationFrame to ensure DOM is updated before getting HTML
        editor.view.dispatch(tr);

        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isInternalUpdateRef.current = false;
              resolve();
            });
          });
        });

        // Persist as rich text to preserve the sensitive mark across round-trips.
        // The HTML should now contain <span data-sensitive-type="...">Benno</span>
        // Backend encoding will replace "Benno" with the code during save
        const htmlToSave = editor.getHTML();
        await saveArtifactImmediately(artifactId, { content: htmlToSave, format: 'richtext' });
      } catch (error) {
        console.error('Failed to encode sensitive data:', error);
        // Could show a toast notification here
      } finally {
        setContextMenu(null); // Close context menu
      }
    },
    [editor, contextMenu, artifactId, saveArtifactImmediately],
  );

  // Close slash menu when clicking outside
  useEffect(() => {
    if (!slashMenu?.visible) return;
    const onMouseDown = (ev: MouseEvent) => {
      if (slashMenuListRef.current?.contains(ev.target as Node)) return;
      closeSlashMenu();
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [slashMenu?.visible, closeSlashMenu]);

  const slashItems: { id: string; label: string }[] = [
    { id: 'heading1', label: 'Heading 1' },
    { id: 'heading2', label: 'Heading 2' },
    { id: 'table', label: 'Table' },
    { id: 'image', label: 'Image' },
    { id: 'math', label: 'Math' },
    { id: 'mermaid', label: 'Mermaid diagram' },
    { id: 'code', label: 'Code block' },
    { id: 'bullet', label: 'Bullet list' },
    { id: 'ordered', label: 'Numbered list' },
    { id: 'quote', label: 'Quote' },
    { id: 'hr', label: 'Divider' },
  ];

  return (
    <div className="flex flex-col h-full bg-white relative">
      <input
        ref={imageFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
        onChange={handleImageFileSelect}
        className="hidden"
        aria-hidden
      />
      {contextMenu && (
        <SensitiveDataContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          selectedText={contextMenu.selectedText}
          onMarkAsSensitive={handleMarkAsSensitive}
          onClose={() => setContextMenu(null)}
        />
      )}
      {slashMenu?.visible && slashMenuAnchor && (
        <div
          ref={slashMenuListRef}
          className="fixed z-50 py-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-[280px] overflow-y-auto min-w-[180px]"
          style={{ top: slashMenuAnchor.top, left: slashMenuAnchor.left }}
        >
          <p className="px-3 py-1.5 text-xs text-neutral-500 border-b border-neutral-100">
            Insert block
          </p>
          {slashItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none"
              onClick={() => runSlashCommand(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      <div
        ref={toolbarRef}
        className="flex flex-nowrap items-center gap-3 px-3 py-3 border-b border-neutral-200 bg-white shadow-sm overflow-x-auto overflow-y-hidden scrollbar-hide"
        onTouchStart={handleToolbarTouchStart}
        onTouchMove={handleToolbarTouchMove}
        onTouchEnd={handleToolbarTouchEnd}
        style={{
          // Prevent page swipe when scrolling toolbar
          touchAction: 'pan-x',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="flex items-center gap-1 rounded-full bg-neutral-100/70 px-2 py-1 border border-neutral-200 shadow-inner shrink-0">
          <ToolbarButton
            onClick={toggle.heading1}
            active={isActive.heading1}
            label="H1"
            title="Heading 1"
          />
          <ToolbarButton
            onClick={toggle.heading2}
            active={isActive.heading2}
            label="H2"
            title="Heading 2"
          />
          <ToolbarButton onClick={toggle.bold} active={isActive.bold} label="B" title="Bold" />
          <ToolbarButton
            onClick={toggle.italic}
            active={isActive.italic}
            label="I"
            title="Italic"
          />
        </div>

        <div className="w-px h-6 bg-neutral-300 shrink-0" />

        <div className="flex items-center gap-1 rounded-full bg-neutral-100/70 px-2 py-1 border border-neutral-200 shadow-inner shrink-0">
          <ToolbarButton
            onClick={toggle.bullet}
            active={isActive.bullet}
            label="• List"
            title="Bullet list"
          />
          <ToolbarButton
            onClick={toggle.ordered}
            active={isActive.ordered}
            label="1. List"
            title="Numbered list"
          />
          <ToolbarButton onClick={toggle.quote} active={isActive.quote} label="❝" title="Quote" />
        </div>

        <div className="w-px h-6 bg-neutral-300 shrink-0" />

        <div className="flex items-center gap-1 rounded-full bg-neutral-100/70 px-2 py-1 border border-neutral-200 shadow-inner shrink-0">
          <ToolbarButton
            onClick={toggle.code}
            active={isActive.code}
            label="{}"
            title="Inline code"
          />
          <ToolbarButton
            onClick={toggle.codeblock}
            active={isActive.codeblock}
            label="Code"
            title="Code block"
          />
        </div>

        <div className="w-px h-6 bg-neutral-300 shrink-0" />

        <div className="flex items-center gap-1 rounded-full bg-neutral-100/70 px-2 py-1 border border-neutral-200 shadow-inner shrink-0">
          <ToolbarButton
            onClick={toggle.table}
            active={isActive.table}
            label="Table"
            title="Insert table"
          />
          <ToolbarButton
            onClick={toggle.insertImage}
            label="Image"
            title="Upload or insert image"
          />
          <ToolbarButton
            onClick={toggle.editMath}
            active={isActive.math}
            label="Math"
            title="Insert or edit math formula"
            icon={<MathIcon className="w-3 h-3" />}
          />
          <ToolbarButton
            onClick={() => toggle.insertMermaid()}
            active={isActive.mermaid}
            label="Diagram"
            title="Insert Mermaid diagram"
            icon={<MermaidIcon className="w-3 h-3" />}
          />
          <span className="text-[11px] text-neutral-500 px-2 whitespace-nowrap">
            Type @ for mentions
          </span>
        </div>

        {isActive.table && (
          <>
            <div className="w-px h-6 bg-neutral-300 shrink-0" />
            <div className="flex flex-nowrap items-center gap-1 rounded-full bg-blue-50 px-2 py-1 border border-blue-200 shadow-inner">
              <ToolbarButton onClick={toggle.addRow} label="+Row" title="Add row after" />
              <ToolbarButton onClick={toggle.addCol} label="+Col" title="Add column after" />
              <ToolbarButton onClick={toggle.delRow} label="−Row" title="Delete current row" />
              <ToolbarButton onClick={toggle.delCol} label="−Col" title="Delete current column" />
              <ToolbarButton onClick={toggle.toggleHeader} label="Hdr" title="Toggle header row" />
              <ToolbarButton onClick={toggle.delTable} label="DelTbl" title="Delete table" />
              <div className="ml-2 flex items-center gap-1 rounded-full bg-white/80 px-2 py-1 border border-blue-200">
                <ToolbarButton
                  onClick={() => toggle.setTableBorder('grid')}
                  active={tableBorder === 'grid'}
                  label="Grid"
                  title="Grid lines"
                />
                <ToolbarButton
                  onClick={() => toggle.setTableBorder('rows')}
                  active={tableBorder === 'rows'}
                  label="Rows"
                  title="Horizontal lines only"
                />
                <ToolbarButton
                  onClick={() => toggle.setTableBorder('cols')}
                  active={tableBorder === 'cols'}
                  label="Cols"
                  title="Vertical lines only"
                />
                <ToolbarButton
                  onClick={() => toggle.setTableBorder('none')}
                  active={tableBorder === 'none'}
                  label="Clean"
                  title="No lines"
                />
              </div>
            </div>
          </>
        )}
      </div>
      {/* Editor content area: center within available workspace space */}
      <div className="flex-1 min-h-0 overflow-y-auto flex justify-center px-4">
        <div className="w-full max-w-4xl">
          <EditorContent editor={editor} />
        </div>
        {/* Table bubble menu: show when cursor is inside a table */}
        {editor && (
          <BubbleMenu
            editor={editor}
            shouldShow={({ editor: ed }) => ed.isActive('table')}
            options={{ placement: 'top' }}
            className="flex flex-nowrap items-center gap-1 py-1 px-2 bg-white border border-neutral-200 rounded-lg shadow-lg"
          >
            <ToolbarButton onClick={toggle.addRow} label="+Row" title="Add row after" />
            <ToolbarButton onClick={toggle.addCol} label="+Col" title="Add column after" />
            <ToolbarButton onClick={toggle.delRow} label="−Row" title="Delete row" />
            <ToolbarButton onClick={toggle.delCol} label="−Col" title="Delete column" />
            <ToolbarButton onClick={toggle.toggleHeader} label="Hdr" title="Toggle header row" />
            <ToolbarButton onClick={toggle.delTable} label="Del table" title="Delete table" />
            <span className="w-px h-5 bg-neutral-200 mx-1" aria-hidden />
            <ToolbarButton
              onClick={() => toggle.setTableBorder('grid')}
              active={tableBorder === 'grid'}
              label="Grid"
              title="Grid lines"
            />
            <ToolbarButton
              onClick={() => toggle.setTableBorder('rows')}
              active={tableBorder === 'rows'}
              label="Rows"
              title="Rows only"
            />
            <ToolbarButton
              onClick={() => toggle.setTableBorder('cols')}
              active={tableBorder === 'cols'}
              label="Cols"
              title="Cols only"
            />
            <ToolbarButton
              onClick={() => toggle.setTableBorder('none')}
              active={tableBorder === 'none'}
              label="None"
              title="No borders"
            />
          </BubbleMenu>
        )}
        {/* Formatting bubble menu: show when text is selected (Notion-like toolbar) */}
        {editor && (
          <BubbleMenu
            editor={editor}
            shouldShow={({ editor: ed }) => !ed.state.selection.empty && !ed.isActive('table')}
            options={{ placement: 'top' }}
            className="flex flex-nowrap items-center gap-0.5 py-1 px-2 bg-white border border-neutral-200 rounded-lg shadow-lg"
          >
            <ToolbarButton
              onClick={toggle.bold}
              active={isActive.bold}
              label="B"
              title="Bold (Ctrl+B)"
            />
            <ToolbarButton
              onClick={toggle.italic}
              active={isActive.italic}
              label="I"
              title="Italic (Ctrl+I)"
            />
            <ToolbarButton
              onClick={toggle.underline}
              active={isActive.underline}
              label="U"
              title="Underline (Ctrl+U)"
            />
            <ToolbarButton onClick={toggle.code} active={isActive.code} label="</>" title="Code" />
            <ToolbarButton
              onClick={() => {
                const url = (e?.getAttributes('link') as { href?: string } | undefined)?.href;
                const href = window.prompt('URL for link', url ?? 'https://');
                if (href != null) toggle.setLink(href.trim() || null);
              }}
              active={isActive.link}
              label="Link"
              title="Add or edit link"
            />
          </BubbleMenu>
        )}
      </div>
    </div>
  );
};

export default RichMarkdownEditor;
